import { useRouter } from 'next/router'
import { ReactElement } from 'react'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'

import { RootState, useSelector, useDispatch } from '@/store'
import { setContactCorporation } from '@/store/contactCorporation/slice'
import { ContactCorporation } from '@/store/contactCorporation/types'
import { theme } from '@/styles/theme'

import { corporationLabels } from './CorporationLabel'
import { Button } from '@/components/elements/Button'
import { FormInput } from '@/components/elements/FormInput'
import { FormLabel } from '@/components/elements/FormLabel'
import { LinkText } from '@/components/elements/LinkText'
import { SectionTitle } from '@/components/elements/SectionTitle'
import { Spacer } from '@/components/elements/Spacer'
import { TitleLayout } from '@/components/layout'
import { Head } from '@/components/layouts/Head'

export const CorporationPage = () => {
  const form = useSelector((state: RootState) => state.contactCorporation)
  const dispatch = useDispatch()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ContactCorporation>()

  const router = useRouter()

  const onSubmit = (data: ContactCorporation) => {
    dispatch(setContactCorporation(data))
    router.push('/contact/corporation/confirm')
  }

  return (
    <>
      <Head
        title="事業・サービスに関するお問い合わせ"
        description="トライアルホールディングスの事業・サービスに関するお問い合わせはこちらから。"
        ogp={{ type: 'article', url: 'contact/corporation/' }}
      />
      <CorporationMain>
        <SectionTitle
          titleText="Corporate"
          subTitleText="法人"
          color="normal"
        />
        <Spacer h={{ pc: 69, sp: 59 }} />

        <Note>
          <ol>
            <li>
              各サービス・子会社へのご提案は、各窓口へ直接お問合せください。
              <br />
              新規事業領域など対象サービスがご不明の場合はその旨を記載のうえ、以下お問合せフォームにてご連絡ください。
            </li>
            <li>
              お問合せの内容によっては、回答に時間を要する場合や、回答しかねる場合がございます。
            </li>
            <li>
              ドメイン指定受信をご利用の場合は、ドメイン@dena.jp、@dena.comからの受信許可設定をお願いいたします。
            </li>
          </ol>
        </Note>
        <Spacer h={{ pc: 100, sp: 69 }} />

        <Form method="post" noValidate onSubmit={handleSubmit(onSubmit)}>
          <FormBackground>
            <FormMain>
              <FormLabelWrapper>
                <FormLabel {...corporationLabels.company} />
              </FormLabelWrapper>
              <FormInput
                id="company"
                type="text"
                register={register('company')}
                defaultValue={form.company}
              />
              <FormLabelWrapper>
                <FormLabel {...corporationLabels.fullName} />
              </FormLabelWrapper>
              <FormInner>
                <FormGroup>
                  <FormLabel {...corporationLabels.lastName} />
                  <FormInput
                    id="lastName"
                    type="text"
                    register={register('lastName', {
                      required: {
                        value: true,
                        message: '入力してください。',
                      },
                    })}
                    errorMessage={errors.lastName}
                    defaultValue={form.lastName}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel {...corporationLabels.firstName} />
                  <FormInput
                    id="firstName"
                    type="text"
                    register={register('firstName', {
                      required: {
                        value: true,
                        message: '入力してください。',
                      },
                    })}
                    errorMessage={errors.firstName}
                    defaultValue={form.firstName}
                  />
                </FormGroup>
              </FormInner>
              <FormLabelWrapper>
                <FormLabel {...corporationLabels.email} />
              </FormLabelWrapper>
              <FormInput
                id="email"
                type="email"
                register={register('email', {
                  required: {
                    value: true,
                    message: '入力してください。',
                  },
                  pattern: {
                    message: '正しい形式で入力してください。',
                    value:
                      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                  },
                })}
                placeholder="例）XXX@XXX.ne.jp"
                errorMessage={errors.email}
                defaultValue={form.email}
              />
              <FormLabelWrapper>
                <FormLabel {...corporationLabels.tel} />
              </FormLabelWrapper>
              <FormInput
                id="tel"
                type="tel"
                maxLength={13}
                register={register('tel', {
                  pattern: {
                    message: '正しい形式で入力してください。',
                    value: /^[-0-9]+$/,
                  },
                })}
                placeholder="例）00-0000-0000"
                errorMessage={errors.tel}
                defaultValue={form.tel}
              />
              <FormLabelWrapper>
                <FormLabel {...corporationLabels.title} />
              </FormLabelWrapper>
              <FormInput
                id="title"
                type="text"
                register={register('title')}
                defaultValue={form.title}
                placeholder="例）XXXについて"
                annotation="初めてではない場合、過去のお問い合わせ内容を簡易にご記入ください。"
              />
              <FormLabelWrapper>
                <FormLabel {...corporationLabels.detail} />
              </FormLabelWrapper>
              <FormComment>
                <FormInput
                  id="detail"
                  type="text"
                  register={register('detail', {
                    required: {
                      value: true,
                      message: '入力してください。',
                    },
                  })}
                  as="textarea"
                  placeholder="お問合せ内容をご入力ください。"
                  errorMessage={errors.detail}
                  defaultValue={form.detail}
                />
                <p>※プロモーションのご提案の場合は、以下のをご記入ください</p>
                <ol>
                  <li>ご提案の概要</li>
                  <li>ご提案の詳細</li>
                  <li>ご提案の期待効果</li>
                </ol>
              </FormComment>
            </FormMain>
            <FormBottom>
              ご記入いただきました個人情報はお問合せ対応のためにご利用させていただきます。
              <br />
              個人情報の取り扱い詳細は「
              <LinkText
                path="/legal/privacy-policy/"
                text="プライバシーポリシー"
              />
              」をご覧ください。
            </FormBottom>
          </FormBackground>
          <ButtonWrapper>
            <SubmitButton btnStyle="outline" type="submit">
              確認する
            </SubmitButton>
          </ButtonWrapper>
        </Form>
      </CorporationMain>
    </>
  )
}

CorporationPage.getLayout = function getLayout(page: ReactElement) {
  const pageTitle = {
    title: 'Inquiry',
    subTitle: 'お問い合わせ',
  }

  const breadCrumbs = [
    { name: 'ホーム', path: '/' },
    { name: 'お問い合わせ', path: '/contact/' },
    { name: '法人', path: '' },
  ]

  return (
    <TitleLayout {...pageTitle} breadCrumbs={breadCrumbs} isRequiredBackground>
      {page}
    </TitleLayout>
  )
}

const CorporationMain = styled.div`
  width: 1040px;
  padding-bottom: 130px;
  margin: 69px auto 0;

  @media screen and (max-width: 1047px) {
    width: 100%;
    margin: 40px auto 0;
    padding: 0 20px 70px;
  }
`

const Note = styled.div`
  text-align: left;
  font-size: 16px;
  line-height: 30px;
  letter-spacing: 0.32px;
  font-weight: 400;

  ol {
    padding-left: 25px;
    list-style-type: disc;
  }

  @media screen and (max-width: 1047px) {
    font-size: 15px;
    line-height: 25px;
    letter-spacing: 0.3px;
  }
`

const Form = styled.form``

const FormMain = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 40px 30px;

  @media screen and (max-width: 1047px) {
    grid-template-columns: 1fr;
    gap: 16px 0;
  }
`

const FormBackground = styled.div`
  background-color: ${theme.colors.backgroundGrey};
  padding: 86px 74px 99px;

  @media screen and (max-width: 1047px) {
    padding: 27px 20px 40px;
  }
`

const FormLabelWrapper = styled.div`
  padding-top: 14px;
`

const FormInner = styled.div`
  display: flex;
  align-items: center;
  column-gap: 43px;

  @media screen and (max-width: 1047px) {
    flex-direction: column;
    align-items: flex-start;
    row-gap: 14px;
  }
`

const FormGroup = styled.div`
  display: flex;
  align-items: center;
  column-gap: 26px;
  width: 50%;

  div {
    flex: 1;
  }

  @media screen and (max-width: 1047px) {
    width: 100%;
  }
`

const FormComment = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 12px;
  line-height: 26px;
  color: ${theme.colors.grey};

  ol {
    padding-left: 25px;
    list-style-type: decimal;
  }
`

const FormBottom = styled.p`
  line-height: 26px;
  letter-spacing: 0.32px;
  margin-top: 74px;
  font-weight: 400;

  @media screen and (max-width: 1047px) {
    margin-top: 36px;
    font-size: 15px;
    line-height: 25px;
    letter-spacing: 0.3px;
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 107px;

  @media screen and (max-width: 1047px) {
    margin-top: 54px;
  }
`

const SubmitButton = styled(Button)`
  width: 198px;
  font-weight: 400;
  border-width: 1px;

  @media screen and (max-width: 1047px) {
    font-size: 18px;
  }
`
